import React, { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import {
  IndianRupee,
  Heart,
  Users,
  TrendingUp,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import apiService from "../services/api";
import { toast } from "react-hot-toast";

const STATUS_COLORS = {
  PAID: "bg-green-100 text-green-700",
  PENDING: "bg-yellow-100 text-yellow-700",
  FAILED: "bg-red-100 text-red-700",
  REFUNDED: "bg-purple-100 text-purple-700",
};

const STATUS_ICONS = {
  PAID: <CheckCircle className="w-3.5 h-3.5" />,
  PENDING: <Clock className="w-3.5 h-3.5" />,
  FAILED: <XCircle className="w-3.5 h-3.5" />,
  REFUNDED: <RefreshCw className="w-3.5 h-3.5" />,
};

function formatINR(amount) {
  return "₹" + Number(amount || 0).toLocaleString("en-IN");
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return format(new Date(dateStr), "dd MMM yyyy");
}

export default function Donations() {
  const [donations, setDonations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchDonations();
  }, [page, statusFilter]);

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const res = await apiService.request("/donations/stats");
      if (res.data) setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch donation stats", err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchDonations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (statusFilter) params.append("status", statusFilter);
      const res = await apiService.request(`/donations?${params}`);
      if (res.data) {
        setDonations(res.data.donations || []);
        setTotalPages(res.data.pages || 1);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      toast.error("Failed to load donations");
    } finally {
      setLoading(false);
    }
  };

  const filteredDonations = donations.filter((d) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      d.donorName?.toLowerCase().includes(q) ||
      d.donorPhone?.includes(q) ||
      d.donorEmail?.toLowerCase().includes(q) ||
      d.razorpayOrderId?.includes(q) ||
      d.razorpayPaymentId?.includes(q)
    );
  });

  const StatCard = ({ icon, label, value, sub, color }) => (
    <div className={`bg-white rounded-2xl p-5 shadow-sm border border-gray-100`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`p-2.5 rounded-xl ${color}`}>{icon}</span>
      </div>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 ">
      {/* Page Header */}
      <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Gaushala Donations</h1>
        <p className="text-gray-500 text-sm">Track all donations and donor information</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {statsLoading ? (
          [1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-3" />
              <div className="h-4 bg-gray-200 rounded w-3/4" />
            </div>
          ))
        ) : (
          <>
            <StatCard
              icon={<IndianRupee className="w-5 h-5 text-green-700" />}
              color="bg-green-100"
              label="Total Amount Raised"
              value={formatINR(stats?.totalAmountRaised)}
              sub="Successful donations only"
            />
            <StatCard
              icon={<Users className="w-5 h-5 text-blue-700" />}
              color="bg-blue-100"
              label="Total Donations"
              value={stats?.totalDonations?.toLocaleString() || "0"}
              sub={`${stats?.successfulDonations || 0} successful`}
            />
            <StatCard
              icon={<TrendingUp className="w-5 h-5 text-purple-700" />}
              color="bg-purple-100"
              label="Success Rate"
              value={
                stats?.totalDonations
                  ? `${Math.round((stats.successfulDonations / stats.totalDonations) * 100)}%`
                  : "—"
              }
              sub="Payment success rate"
            />
          </>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, phone, email, order ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
          >
            <option value="">All Status</option>
            <option value="PAID">Paid</option>
            <option value="PENDING">Pending</option>
            <option value="FAILED">Failed</option>
            <option value="REFUNDED">Refunded</option>
          </select>

          <button
            onClick={() => { fetchDonations(); fetchStats(); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-700 text-white rounded-xl text-sm font-medium hover:bg-green-800 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3.5">Donor</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3.5">Amount</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3.5">Status</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3.5 hidden md:table-cell">Order ID</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3.5 hidden lg:table-cell">Message</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3.5">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array(8).fill(0).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-5 py-4"><div className="h-4 bg-gray-200 rounded w-32" /></td>
                    <td className="px-5 py-4"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                    <td className="px-5 py-4"><div className="h-5 bg-gray-200 rounded-full w-16" /></td>
                    <td className="px-5 py-4 hidden md:table-cell"><div className="h-4 bg-gray-200 rounded w-36" /></td>
                    <td className="px-5 py-4 hidden lg:table-cell"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                    <td className="px-5 py-4"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                  </tr>
                ))
              ) : filteredDonations.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-gray-400">
                    <Heart className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No donations found</p>
                  </td>
                </tr>
              ) : (
                filteredDonations.map((d) => (
                  <tr key={d.id} className="hover:bg-gray-50 transition-colors">
                    {/* Donor */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-green-700 font-semibold text-sm">
                            {d.isAnonymous ? "?" : (d.donorName?.[0] || d.user?.profile?.name?.[0] || "G")}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">
                            {d.isAnonymous ? "Anonymous" : (d.donorName || d.user?.profile?.name || "Guest")}
                          </p>
                          <p className="text-xs text-gray-400">
                            {d.donorPhone || d.donorEmail || d.user?.phone || d.user?.email || "—"}
                          </p>
                        </div>
                      </div>
                    </td>
                    {/* Amount */}
                    <td className="px-5 py-4">
                      <span className="text-sm font-bold text-green-700">{formatINR(d.amount)}</span>
                    </td>
                    {/* Status */}
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[d.status] || "bg-gray-100 text-gray-600"}`}>
                        {STATUS_ICONS[d.status]}
                        {d.status}
                      </span>
                    </td>
                    {/* Order ID */}
                    <td className="px-5 py-4 hidden md:table-cell">
                      <span className="text-xs text-gray-500 font-mono">{d.razorpayOrderId}</span>
                    </td>
                    {/* Message */}
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <span className="text-xs text-gray-500 max-w-[140px] truncate block">
                        {d.message || "—"}
                      </span>
                    </td>
                    {/* When */}
                    <td className="px-5 py-4">
                      <span className="text-xs text-gray-400">{timeAgo(d.createdAt)}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Showing {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} of {total} donations
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-gray-700">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
